import {kapitelDateString, kapitelDateTimeString, kapitelMonthString, kapitelTimeString} from "@/graphql/kapitelTypes";
import {
    AInesAssistantType,
    type AvailabilityCoordinate,
    AvailabilityCoordinateInput,
    AvailabilityFragment,
    Day,
    DemandJoDaSh,
    DemandJoDaShFragment,
    DemandJoDaShTiFragment,
    Jobsite,
    JobsiteInput,
    ShiftType
} from "@/graphql/generated/graphql";
import datetime, {kapitelDateTimeFormatTime} from "@/helper/datetime/datetime";
import date from "@/helper/datetime/date";
import {query} from "@/graphql/client";
import {jobsiteFragment} from "@/helper/jobsite";
import {useGlobalEmitter} from "@/helper/emitter";
import time from "@/helper/datetime/time";
import {formatRange} from "@/helper/datetime/dateRange";
import {AvailabilityChangeAttemptResult, canAddAvailabilityToDay, fetchDayForDate} from "@/helper/day";
import {consoleErrorChat} from "@/helper/console";
import {kapitelErrorHandler} from "@/helper/error";
import {generateAvailability} from "@/helper/availability";
import {setAvailabilityAbsence} from "@/helper/availabilityAbsence";
import {sendUserMessage, setUpAssistant} from "@/helper/chat/chatBL";
import {graphql} from "@/graphql/generated";

graphql(`
    fragment DemandJoDaSh on DemandJoDaSh {
        date
        shiftType
        inesIsConsidering
        jobsite { ...JobsiteFragment }
    }`)
    

graphql(`
    fragment DemandJoDaShTi on DemandJoDaShTi {
        date
        begin
        until
        shiftType
        jobsite { ...JobsiteFragment }
    }`)

graphql(`
    fragment AvailabilityCoordinateFragment on AvailabilityCoordinate {
        date
        begin
        until
        shiftType
        jobsite { ...JobsiteFragment }
    }`)


export const fetchPastDemandForMonth = async (
    jobsiteId: number,
    m: kapitelMonthString,
    shifts: ShiftType[] | undefined,
    fetchOptions?: RequestInit
) => fetchPastDemandForDateRange(jobsiteId, m, date.endOfMonth(m), shifts, fetchOptions)

export const fetchPastDemandForDateRange = async (
    jobsiteId: number,
    begin: kapitelDateString,
    until: kapitelDateString,
    shifts?: ShiftType[],
    fetchOptions?: RequestInit
) : Promise<DemandJoDaShFragment[]> => {

    const result = await query(
        graphql(`query GetPastDemandJoDaShs (
                $begin: KapitelDateImmutable!
                $until: KapitelDateImmutable!
                $shifts: [ShiftType!]!
                $jobsiteId: Int!
            ) {
                pastDemandJoDaSh(begin: $begin, until: $until, jobsiteId: $jobsiteId, shiftSelection: $shifts) {
                    ...DemandJoDaSh
                }
            }`), {
            begin,
            until,
            jobsiteId,
            shifts,
        },
        {fetchOptions}
    )

    if (!result?.data?.pastDemandJoDaSh) {
        throw new Error("no DemandJoDaShs in response")
    }

    return result.data.pastDemandJoDaSh as DemandJoDaShFragment[]
}

export const fetchDemandForMonth = async (
    jobsiteId: number,
    m: kapitelMonthString,
    shifts: ShiftType[] | undefined,
    fetchOptions: RequestInit | undefined = undefined
) => fetchDemandForDateRange(jobsiteId, m, date.endOfMonth(m), shifts, fetchOptions)

export const fetchDemandForDate = async (
    jobsiteId: number,
    d: kapitelDateString,
    shifts: ShiftType[] | undefined,
    fetchOptions: RequestInit | undefined = undefined
) => fetchDemandForDateRange(jobsiteId, d, d, shifts, fetchOptions)

export const fetchDemandForDateRange = async (
    jobsiteId: number,
    begin: kapitelDateString,
    until: kapitelDateString,
    shifts?: ShiftType[],
    fetchOptions: RequestInit | undefined = undefined
) => {
    const result = await query(
        graphql(`query getDemandJoDaShs (
                $begin: KapitelDateImmutable!
                $until: KapitelDateImmutable!
                $shifts: [ShiftType!]!
                $jobsiteId: Int!
            ) {
                demandJoDaSh(begin: $begin, until: $until, jobsiteId: $jobsiteId, shiftSelection: $shifts) {
                    ...DemandJoDaSh
                }
            }`), {
            begin,
            until,
            jobsiteId,
            shifts
        },
        {fetchOptions}
    )

    const r = result?.data?.demandJoDaSh;
    if (!r) {
        throw new Error("no demandJoDaSh in response")
    }

    return r as DemandJoDaShFragment[]
}

export const getDemandJoDaShTiForDateRange = async (
    jobsiteId: number,
    begin: kapitelDateString,
    until: kapitelDateString,
    shifts?: ShiftType[]
): Promise<DemandJoDaShTiFragment[]> => {

    const result = await query(
        graphql(`query GetDemandJoDaShTi (
                $begin: KapitelDateImmutable!
                $until: KapitelDateImmutable!
                $shifts: [ShiftType!]
                $jobsiteId: Int!
            ) {
                demandJoDaShTi(begin: $begin, until: $until, jobsiteId: $jobsiteId, shiftSelection: $shifts) {
                    ...DemandJoDaShTi
                }
            }`), {
            begin,
            until,
            jobsiteId,
            shifts,
        }
    )

    const r = result?.data?.demandJoDaShTi;
    if (!r) {
        throw new Error("no availabilityCoordinates in response")
    }

    return r as DemandJoDaShTiFragment[]
}

export const fetchConsideredFor = async (demandJoDaShTi: DemandJoDaShTiFragment) => {
    const jobsite = {id: demandJoDaShTi.jobsite.id}
    const demandJaDaShTi = {
        begin: demandJoDaShTi.begin,
        until: demandJoDaShTi.until,
        jobsite,
    }
    const result = await query(
        graphql(`query DoesInesConsiderThisDemandJoDaShTi(
                $demandJaDaShTi: DemandJoDaShTiInput!
            ) {
                doesInesConsiderThisDemandJoDaShTi(demandJoDaShTi: $demandJaDaShTi)
            }`), {
            demandJaDaShTi
        }
    )

    const r = result?.data?.doesInesConsiderThisDemandJoDaShTi;

    return r
}

export type JoDaShTiIdentifier = {
    begin: kapitelDateTimeString,
    until: kapitelDateTimeString,
    jobsite: { id: number }
}

export function joDaShTiMatcher(a: JoDaShTiIdentifier, b: JoDaShTiIdentifier): boolean {
    return (
        a.jobsite.id === b.jobsite.id
        && datetime.isSame(a.begin, b.begin)
        && datetime.isSame(a.until, b.until)
    )
}

export const containsJoDaShTi = (a: JoDaShTiIdentifier[], b: JoDaShTiIdentifier) => !!a.find((item) => joDaShTiMatcher(item, b))

export const  removeAvailabilityCoordinateFromDay = async (demandJoDaShTi: DemandJoDaShTiFragment,  day: Day|null) : Promise<AvailabilityChangeAttemptResult> => {
    if(!day){
        day = await fetchDayForDate(demandJoDaShTi.date) ?? null
        if(!day){
            const err = "no day object found."
            consoleErrorChat(err)
            kapitelErrorHandler(err)
            throw err
        }
    }

    if(day?.availability && day?.availability.additionalAvailabilityCoordinates) {
        const index = day.availability.additionalAvailabilityCoordinates.findIndex(item => joDaShTiMatcher(item, demandJoDaShTi))
        if (index === -1) return {success: true};

        // remove and return the removed item
        day.availability.additionalAvailabilityCoordinates.splice(index, 1)[0];
        await setAvailabilityAbsence(day.availability, null)
        useGlobalEmitter().emit('day:mutated', day.date)
    }
    return {success: true}
}

export const addAvailabilityCoordinateToDay = async (demandJoDaShTi: DemandJoDaShTiFragment, day: Day|null): Promise<AvailabilityChangeAttemptResult>  =>
{
    if(!day){
        day = await fetchDayForDate(demandJoDaShTi.date) ?? null
        if(!day){
            const err = "no day object found."
            consoleErrorChat(err)
            kapitelErrorHandler(err)
            throw err
        }
    }

    const avaiabilityable = canAddAvailabilityToDay(day)
    if(avaiabilityable.success){
        let availability;
        if(day.availability){
            availability = day.availability as AvailabilityFragment
        }else {
            availability = generateAvailability(demandJoDaShTi.date) as AvailabilityFragment
        }
        availability.additionalAvailabilityCoordinates.push({
            ...demandJoDaShTi,
            __typename: 'AvailabilityCoordinate',
        })
        await setAvailabilityAbsence({
            ...availability,
            excludedJobsites: availability.excludedJobsites ? availability.excludedJobsites as Jobsite[] : [],
            additionalAvailabilityCoordinates: availability.additionalAvailabilityCoordinates as AvailabilityCoordinate[]
        }, null)
        useGlobalEmitter().emit('day:mutated', day.date)
    }
    return avaiabilityable
}

export const parseCoordinateString = (input: string): AvailabilityCoordinateInput|AvailabilityCoordinate => {

    const params = new URLSearchParams(input.trim().replace(/^\?/, ""));

    const jobsiteToken = params.get("j") ?? params.get("jobsite");
    const dateStr = params.get("d") as kapitelDateString | null;
    const beginTime = params.get("b") as kapitelTimeString | null;
    const untilTime = params.get("u") as kapitelTimeString | null;

    if (!jobsiteToken || !dateStr || !beginTime || !untilTime) {
        throw new Error("Missing j (jobsite), d (date), b (begin), or u (until)");
    }

    time.validateKapitelTime(beginTime)
    time.validateKapitelTime(untilTime)
    date.validateKapitelDate(dateStr)

    const begin = datetime.convertDateAndTime2DateTime(dateStr, beginTime);
    const untilDate: kapitelDateString = untilTime < beginTime ? date.addDays(dateStr, 1) : dateStr
    const until = datetime.convertDateAndTime2DateTime(untilDate, untilTime);

    const jobsiteId = /^\d+$/.test(jobsiteToken) ? Number(jobsiteToken) : jobsiteToken;
    const jobsite = ({ id: jobsiteId } as unknown) as JobsiteInput;

    return { begin, until, jobsite };
};



export const stringifyAvailabilityCoordinate = (ac: AvailabilityCoordinate): string => {
    if (!ac) throw new TypeError("AvailabilityCoordinate is required");

    const d = datetime.convertDateTime2Date(ac.begin); // YYYY-MM-DD
    const b = datetime.formatTime(ac.begin, kapitelDateTimeFormatTime); // HH:mm
    const u = datetime.formatTime(ac.until, kapitelDateTimeFormatTime); // HH:mm
    const j = String(ac.jobsite.id)

    const params = new URLSearchParams();
    params.set("j", j);
    params.set("d", d);
    params.set("b", b);
    params.set("u", u);

    return params.toString();
};


const FEW_MAX = 4 as const;

export function getAvailabilityCoordinatesSummaryLabelForJobsite(
    coords: DemandJoDaSh[]
): string {
    if (!coords || coords.length === 0) return "Keine Verfügbarkeiten";

    // normalize + sort by ISO date
    const items = coords
        .map((c) => {
            const d: kapitelDateString = c.date;
            return {
                ...c,
                _date: d,
                _dateLabel: date.formatKapitelDate(d, "d.L."),
            };
        })
        .sort((a, b) => (a._date < b._date ? -1 : a._date > b._date ? 1 : 0));

    const total = items.length;

    // 1) Single
    if (total === 1) {
        const i = items[0];
        return `${i._dateLabel}: ${i.shiftType}`;
    }

    // group by shiftType
    const byShift = new Map<string, typeof items>();
    for (const it of items) {
        const key = it.shiftType ?? "";
        const arr = byShift.get(key);
        if (arr) arr.push(it);
        else byShift.set(key, [it]);
    }

    // 2) Few (2..FEW_MAX): "3 Einsätze: Früh (1.10., 3.10.) · Spät (2.10.)"
    if (total <= FEW_MAX) {
        const parts: string[] = [];
        for (const [shift, arr] of byShift.entries()) {
            const dates = arr.map((a) => a._dateLabel).join(", ");
            parts.push(`${shift} (${dates})`);
        }
        return `${total} Einsätze: ${parts.join(" · ")}`;
    }

    // 3) Many (>= FEW_MAX+1): first–last date per shift (gaps allowed)
    const parts: string[] = [];
    for (const [shift, arr] of byShift.entries()) {
        // unique ISO dates, sorted
        const datesSorted = Array.from(new Set(arr.map((a) => a._date))).sort();
        const first = datesSorted[0]!;
        const last = datesSorted[datesSorted.length - 1]!;
        parts.push(`${shift} ×${arr.length} (${formatRange(first, last)})`);
    }
    return `${total} Einsätze: ${parts.join(", ")}`;
}

export const askChatForDemandOn = async (date: kapitelDateString) => {
    await setUpAssistant()
    await sendUserMessage(
        "Zeig mir offene Dienste für den " + date + ".",
        {
            messageExpertAssistant:AInesAssistantType.EmployeeDemandBrowser,
            isScriptedContent: true
        })
    useGlobalEmitter().emit('CloseAllModals')
}
export const convertToDemandJoDaShTi = (d: {
    begin: kapitelDateTimeString,
    until: kapitelDateTimeString,
    date: kapitelDateString,
    jobsite: Jobsite,
    shiftType: ShiftType
}): DemandJoDaShTiFragment => ({__typename: "DemandJoDaShTi", ...d})
